import React from 'react'
import { compose, map, mapValues, includes, xor } from 'lodash/fp'
import moment from 'moment'
import styled from 'styled-components'
import { navigate } from 'gatsby'
import { Helmet } from 'react-helmet'

import Search from '../../components/Search/index'
import Layout from '../../components/Layout'
import Button from '../../components/Button'
import Heading from '../../components/Heading'
import Table from '../../components/Table'
import { isEmptyObject } from '../../utils/isEmptyObject'
import { isEqual } from 'lodash/fp'
import { AppContext } from '../App'
import Checkbox from '../../components/Checkbox'
import FiltersForm from './Form'
import filtersIcon from '../../images/filtersIcon.svg'
import addIconRed from '../../images/addIconRed.svg'
import withLoading from '../../utils/withLoading'
import withPermissions from '../../containers/withPermissions'
import { normalizeSortState } from '../../components/Table/convertSortState'
import { genders } from './constants'
import withQueryParams from '../../utils/withQueryParams'
import { permissions as p } from '../../utils/constants'
import { Hidden } from '../../styles/Shared'

const LoadingTable = withLoading(Table)

const decorate = compose(withPermissions, withQueryParams)

class PatientsList extends React.Component {
  static contextType = AppContext

  state = {
    sort: {},
    ready: false,
    patients: [],
    ids: [],
    meta: {
      limit: 20,
      page: 0,
    },
    models: [],
    filters: { search: '' },
    selection: [],
  }

  fetchPatients = async (resetPage = true) => {
    const { userRoles, unitId } = this.context
    const { filters, meta, sort } = this.state
    this.props.setParams({ filters, meta, sort })

    const endpoint = userRoles.includes('unitDirector') // TODO this check is unecessary
      ? '/diagnostic/rest/patient/search'
      : '/diagnostic/rest/patient/my/search'

    this.setState(
      {
        ready: false,
        filters: { ...this.state.filters, unitId: unitId },
        meta: {
          ...this.state.meta,
          page: resetPage ? 0 : this.state.meta.page,
        },
      },
      async () => {
        const { meta } = this.state
        try {
          const response = await this.context.withAuth(endpoint, {
            method: 'POST',
            payload: {
              filters: {
                ...this.state.filters,
              },

              meta: {
                ...meta,
                sorts: isEmptyObject(this.state.sort)
                  ? [{ column: 'id', asc: false }]
                  : normalizeSortState(this.state.sort),
              },
              models: [...this.state.models],
            },
          })

          const ids = map('id', response.models)

          this.setState({
            ids,
            models: response.models,
            meta: response.meta,
          })
        } catch (e) {
          console.log(e)
        }
        this.setState({ ready: true })
      }
    )
  }

  onPageChange = (index) => {
    this.setState({ meta: { ...this.state.meta, page: index } }, () =>
      this.fetchPatients(false)
    )
  }

  onFiltersChange = (filters) => {
    this.setState({ filters }, () => this.fetchPatients())
  }

  setItemSelection = (items) => this.setState({ selection: items })

  onSortedChange = (sort) => {
    Object.entries(sort)
      .map((elements) => elements)[0]
      ?.includes('days')
      ? this.setState({ sort: { ...sort, dateOfBirth: sort.days } }, () => {
          this.fetchPatients(false)
        })
      : this.setState({ sort }, () => {
          this.fetchPatients(false)
        })
  }

  onSearchChange = (search) => {
    this.setState(
      (prevState) => ({
        ...prevState,
        filters: { ...prevState.filters, search },
        meta: { ...prevState.meta, page: 0 },
      }),
      () => this.fetchPatients()
    )
  }

  componentDidMount() {
    const { params } = this.context
    const searchParams = this.props.getParams()
    try {
      const mappedParams = mapValues(JSON.parse, searchParams)
      this.setState({ ...params, ...mappedParams }, () =>
        this.fetchPatients(false)
      )
    } catch (e) {
      this.fetchPatients()
    }
  }

  componentDidUpdate() {
    const { updateParams, params } = this.context
    if (!isEqual(params, this.state)) {
      updateParams(this.state)
    }
  }

  render() {
    const { toggleFilters } = this.context

    return (
      <Layout
        filters={
          <FiltersForm
            initialValues={this.state.filters}
            onClose={toggleFilters}
            onSubmit={this.onFiltersChange}
          />
        }
      >
        <Helmet htmlAttributes={{ lang: 'pl-PL' }}>
          <title>Osoby Diagnozowane</title>
        </Helmet>
        <>
          <Heading title="Osoby Diagnozowane">
            <div style={{ width: '100%' }}>
              <label>
                <Search
                  width={450}
                  placeholder="Szukaj"
                  ariaLabel={
                    this.state.filters.search
                      ? `Szukaj liczba znalezionych wyników: ${
                          this.state.models != null
                            ? this.state.models.length
                            : 0
                        }`
                      : 'Szukaj'
                  }
                  input={{
                    onChange: this.onSearchChange,
                    value: `${this.state.filters.search}`,
                  }}
                />
              </label>
            </div>
            {this.props.hasPermissions(p.diagnostician) && (
              <Button
                marginLeft
                icon={addIconRed}
                label="Nowa osoba diagnozowana"
                link="/app/patients/add"
                color="white"
                altTag=""
                imgAriaHidden={true}
              />
            )}

            <Button
              icon={filtersIcon}
              color="white"
              label="Filtruj"
              onClick={toggleFilters}
              marginLeft
              altTag=""
              imgAriaHidden={true}
            />
          </Heading>
          <LoadingTable
            page={this.state.meta.page}
            defaultPageSize={this.state.meta.limit}
            ready={this.state.ready}
            getLoadingProps={() => ({
              'aria-hidden': true,
              role: 'none',
            })}
            data={this.state.models}
            total={this.state.meta.total}
            onPageChange={this.onPageChange}
            onSortedChange={this.onSortedChange}
            getTheadThProps={(state, _undefined, column) => ({
              style: {
                maxWidth: column.id === 'diagLink' ? '10px' : 'auto',
              },
            })}
            order={this.state.sort}
            addAriaLabelToHeader={true}
            getTrProps={(_, rowInfo) => ({
              style: {
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
              },
              onClick: function () {
                navigate(`/app/patients/${rowInfo.original.id}`)
              },
              'aria-label': 'Przejdź do osoby diagnozowanej',
            })}
            getTheadTrProps={() => ({
              style: { display: 'flex', alignItems: 'center' },
            })}
            columns={[
              {
                id: 'cumulation',
                sortable: false,
                resizable: false,
                Header: (
                  <Checkbox
                    label={false}
                    aria-label="Zaznacz wszystkie osoby diagnozowane na stronie"
                    input={{
                      value:
                        xor(this.state.ids, this.state.selection).length === 0,
                      onChange: () => {
                        this.setItemSelection(
                          xor(this.state.ids, this.state.selection).length === 0
                            ? []
                            : this.state.ids
                        )
                      },
                    }}
                  />
                ),
                Cell: ({ original }) => (
                  <Checkbox
                    label={false}
                    aria-label="Zaznacz osobę"
                    input={{
                      value: includes(original.id, this.state.selection),
                      onChange: () =>
                        this.setItemSelection(
                          xor([original.id], this.state.selection)
                        ),
                    }}
                  />
                ),

                getProps: () => ({
                  onClick: (e) => e.stopPropagation(),
                }),
              },
              {
                Header: 'ID',
                id: 'id',
                accessor: 'id',
                sortable: true,
                getProps: () => ({
                  role: undefined,
                }),
              },
              {
                id: 'firstName',
                Header: 'Imię',
                accessor: 'person.firstName',
                sortable: true,
                getProps: () => ({
                  role: undefined,
                }),
              },
              {
                id: 'lastName',
                accessor: 'person.lastName',
                Header: 'Nazwisko',
                sortable: true,
                getProps: () => ({
                  role: undefined,
                }),
              },
              {
                id: 'days',
                Header: 'Wiek(dni)',
                sortable: true,
                accessor: ({ person }) => {
                  const dateFrom = moment(person.dateOfBirth)
                  const today = moment()

                  return today.diff(dateFrom, 'days')
                },
                getProps: () => ({
                  role: undefined,
                }),
              },
              {
                id: 'dateOfBirth',
                accessor: 'person.dateOfBirth',
                Header: 'Data Urodzenia',
                sortable: true,
                getProps: () => ({
                  role: undefined,
                }),
              },
              {
                id: 'gender',
                accessor: ({ person }) => genders[person.gender],
                Header: 'Płeć',
                sortable: true,
                getProps: () => ({
                  role: undefined,
                }),
              },
              {
                id: 'diagLink',
                Header: <Hidden>Link do osoby diagnozowanej</Hidden>,
                sortable: false,
                resizable: false,
                getProps: () => ({
                  role: undefined,
                }),
                Cell: ({ original, _undefined, row }) => (
                  <HiddenButton
                    tableRowIndex={row._index}
                    role="gridcell"
                    aria-label={`Przejdź do osoby diagnozowanej z ID ${original.id}`}
                    onClick={() => navigate(`/app/patients/${original.id}`)}
                  />
                ),
              },
            ]}
          />
        </>
      </Layout>
    )
  }
}

const HiddenButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  width: 102vw;
  position: absolute;
  top: ${(props) => 55 + props.tableRowIndex * 64}px;
  height: 63px;
  margin-left: -90vw;
`

export default decorate(PatientsList)
